import { KeyboardEvent, ReactNode, useRef } from 'react';

export const INSIGHT_TABS = [
  { key: 'funnel', label: 'Funnel' },
  { key: 'ads', label: 'Ads' },
  { key: 'channels', label: 'Channels' },
  { key: 'supply', label: 'Supply' },
  { key: 'retention', label: 'Value & retention' },
  { key: 'alerts', label: 'Alerts' },
] as const;
export type InsightTab = typeof INSIGHT_TABS[number]['key'];

export function resolveTab(raw: string | null): InsightTab {
  return INSIGHT_TABS.find((t) => t.key === raw)?.key ?? 'funnel';
}

interface Props {
  active: InsightTab;
  onSelect: (tab: InsightTab) => void;
  children: ReactNode;
}

/**
 * Spec 084 — the six insights tabs (`admin-tabs` look: navy text, yellow underline). `role="tablist"` with
 * roving tabindex: Arrow Left/Right move and select (mirrored in RTL), Home/End jump to the ends.
 * The page keeps the active tab in `?tab=`.
 */
export default function InsightsTabs({ active, onSelect, children }: Props) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});
  const onKey = (e: KeyboardEvent<HTMLDivElement>) => {
    const i = INSIGHT_TABS.findIndex((t) => t.key === active);
    const rtl = typeof document !== 'undefined' && document.documentElement.dir === 'rtl';
    const n = INSIGHT_TABS.length;
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = rtl ? i - 1 : i + 1;
    else if (e.key === 'ArrowLeft') next = rtl ? i + 1 : i - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = n - 1;
    if (next === null) return;
    e.preventDefault();
    const key = INSIGHT_TABS[(next + n) % n].key;
    onSelect(key);
    refs.current[key]?.focus();
  };
  return (
    <div className="mkt-insights">
      <div className="admin-tabs mkt-itabs" role="tablist" aria-label="Marketing insights" onKeyDown={onKey} data-testid="insights-tabs">
        {INSIGHT_TABS.map((t) => (
          <button key={t.key} ref={(el) => { refs.current[t.key] = el; }} type="button" role="tab" id={`mkt-itab-${t.key}`}
            aria-selected={active === t.key} aria-controls="mkt-itab-panel" tabIndex={active === t.key ? 0 : -1}
            className={`mkt-itab${active === t.key ? ' mkt-itab--active' : ''}`} onClick={() => onSelect(t.key)}
            data-testid={`insights-tab-${t.key}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div role="tabpanel" id="mkt-itab-panel" aria-labelledby={`mkt-itab-${active}`} tabIndex={0} className="mkt-itab-panel"
        data-testid={`insights-panel-${active}`}>
        {children}
      </div>
    </div>
  );
}
